export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: init.body ? { 'Content-Type': 'application/json', ...init.headers } : init.headers,
  });
  if (res.status === 401 && path !== '/api/auth') onUnauthorized?.();
  if (!res.ok) {
    let message = `request failed (${res.status})`;
    try {
      const data = (await res.json()) as { error?: string };
      if (data.error) message = data.error;
    } catch {
      /* keep default */
    }
    throw new ApiError(res.status, message);
  }
  return (await res.json()) as T;
}

export type Note = {
  id: number;
  recipe_slug: string;
  author: string | null;
  body: string;
  created_at: string;
  updated_at: string;
};

export const api = {
  login: (pin: string) => request<{ ok: boolean }>('/api/auth', { method: 'POST', body: JSON.stringify({ pin }) }),
  session: () => request<{ authed: boolean }>('/api/session'),
  stars: () => request<Record<string, boolean>>('/api/stars'),
  setStar: (slug: string, starred: boolean) =>
    request<{ slug: string; starred: boolean }>(`/api/stars/${slug}`, {
      method: 'PUT',
      body: JSON.stringify({ starred }),
    }),
  servings: () => request<Record<string, { servings: number; scaled_by: string | null }>>('/api/servings'),
  setServings: (slug: string, servings: number, scaledBy?: string) =>
    request<{ slug: string; servings: number }>(`/api/servings/${slug}`, {
      method: 'PUT',
      body: JSON.stringify({ servings, scaled_by: scaledBy }),
    }),
  clearServings: (slug: string) =>
    request<{ slug: string; servings: null }>(`/api/servings/${slug}`, {
      method: 'PUT',
      body: JSON.stringify({ servings: null }),
    }),
  progress: () => request<Record<string, { steps: number[]; ingredients: string[] }>>('/api/progress'),
  setProgress: (slug: string, steps: number[], ingredients: string[]) =>
    request<{ slug: string; steps: number[]; ingredients: string[] }>(`/api/progress/${slug}`, {
      method: 'PUT',
      body: JSON.stringify({ steps, ingredients }),
    }),
  clearProgress: (slug: string) =>
    request<{ slug: string }>(`/api/progress/${slug}`, { method: 'PUT', body: JSON.stringify({ clear: true }) }),
  plan: () => request<Record<string, { servings: number | null; added_by: string | null }>>('/api/plan'),
  setPlan: (slug: string, servings: number | null, addedBy?: string) =>
    request<{ slug: string }>(`/api/plan/${slug}`, { method: 'PUT', body: JSON.stringify({ servings, added_by: addedBy }) }),
  removePlan: (slug: string) => request<{ slug: string }>(`/api/plan/${slug}`, { method: 'DELETE' }),
  clearPlan: () => request<{ ok: boolean }>('/api/plan', { method: 'DELETE' }),
  notes: (slug: string) => request<Note[]>(`/api/notes/${slug}`),
  addNote: (slug: string, note: { author?: string; body: string }) =>
    request<Note>(`/api/notes/${slug}`, { method: 'POST', body: JSON.stringify(note) }),
  editNote: (id: number, body: string) =>
    request<Note>(`/api/notes/${id}`, { method: 'PATCH', body: JSON.stringify({ body }) }),
  deleteNote: (id: number) => request<{ ok: boolean }>(`/api/notes/${id}`, { method: 'DELETE' }),
};
